import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import * as stateSelectors from '../../blk-model-subscription-manger.selectors';
import { UserDataHandlerService } from '../../services/store-data-loader.service';
import { UserDataService } from '../../services/user-data.service';
import * as saveActions from '../saved-model-portfolios/saved-model-portfolios.actions';
import * as subscribeActions from '../subscribed-model-portfolios/subscribed-model-portfolios.actions';
import * as saveAndSubscribeActions from './save-and-subscribe-model-portfolios.actions';

@Injectable()
export class SaveAndSubscribeModelPortfoliosEffects {
	
	public saveAndSubscribeModelPortfolio: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType<saveAndSubscribeActions.SaveAndSubscribeModelApiRequest>(saveAndSubscribeActions.SAVE_AND_SUBSCRIBE_MODEL_API_REQUEST),
		switchMap(action => {
			console.log(`Saving and subscribing in effect:  ${action.portfolioId}`);
			return this.userDataService.saveAndSubscribeBlkModelPortfolioToAPI(action.portfolioId, action.origin).pipe(
				map(responseJson => {
					const newSaveMapping: Map<string, string> = this.userDataHandler.getNewMapping(responseJson, action.portfolioId, 0);
					const newSubscribeMapping: Map<string, string> = this.userDataHandler.getNewMapping(responseJson, action.portfolioId, 1);

					if (newSaveMapping.size && newSubscribeMapping.size) {
						return {
							type: saveAndSubscribeActions.SAVE_AND_SUBSCRIBE_MODEL_API_REQUEST_SUCCESS,
							newSaveMapping: newSaveMapping,
							newSubscribeMapping: newSubscribeMapping
						} as Action;
					} else {
						return {
							type: saveAndSubscribeActions.SAVE_AND_SUBSCRIBE_MODEL_API_REQUEST_FAILURE
						} as Action;
					}
				})
			);
		}),
		catchError(error => {
			console.error(`[ERROR] Failed to save and subscribe to model: ${error}`);
			return of({
				type: saveAndSubscribeActions.SAVE_AND_SUBSCRIBE_MODEL_API_REQUEST_FAILURE
			} as Action);
		})
	));

	
	public saveAndSubscribeModelPortfolioSuccess: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType<saveAndSubscribeActions.SaveAndSubscribeModelApiRequestSuccess>(saveAndSubscribeActions.SAVE_AND_SUBSCRIBE_MODEL_API_REQUEST_SUCCESS),
		switchMap(action => [
			{
				type: saveActions.ADD_SAVED_STORE_MAPPING,
				newMapping: action.newSaveMapping
			} as Action,
			{
				type: subscribeActions.ADD_SUBSCRIPTION_STORE_MAPPING,
				newMapping: action.newSubscribeMapping
			} as Action
		])
	));

	
	public unsubscribeModelPortfolio = createEffect(() => this.actions$.pipe(
		ofType<saveAndSubscribeActions.UnsaveAndUnsubscribeModelApiRequest>(saveAndSubscribeActions.UNSAVE_AND_UNSUBSCRIBE_MODEL_API_REQUEST),
		withLatestFrom(this.store$.pipe(select(stateSelectors.getSavedModelPortfolioIdMappings)), this.store$.pipe(select(stateSelectors.getSubscribedModelPortfolioIdMappings))),
		switchMap(([action, savedMappings, savedSubscribedMappings]) => {
			const savedUniqueId: string = savedMappings.get(action.portfolioId);
			const subscribedUniqueId: string = savedSubscribedMappings.get(action.portfolioId);
			console.log(`Unsaving and unsubscribing in effect, portfolioId:  ${action.portfolioId}, uniqueId: ${savedUniqueId} ${subscribedUniqueId}`);

			// filtering out undefined uniqueIDs since there is no single unsave option
			return this.userDataService.unsaveAndUnsubscribeBlkModelPortfolioToAPI([savedUniqueId, subscribedUniqueId].filter(element => element)).pipe(
				map(responseJson => {
					const successful: boolean = this.userDataHandler.isDeleteSuccessful(responseJson);
					if (successful) {
						return {
							type: saveAndSubscribeActions.UNSAVE_AND_UNSUBSCRIBE_MODEL_API_REQUEST_SUCCESS,
							portfolioId: action.portfolioId
						} as Action;
					} else {
						return {
							type: saveAndSubscribeActions.UNSAVE_AND_UNSUBSCRIBE_MODEL_API_REQUEST_FAILURE
						} as Action;
					}
				})
			);
		}),
		catchError(error => {
			return of({
				type: saveAndSubscribeActions.UNSAVE_AND_UNSUBSCRIBE_MODEL_API_REQUEST_FAILURE
			} as Action);
		})
	));

	
	public unaveAndUnsubscribeModelPortfolioSuccess: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType<saveAndSubscribeActions.UnsaveAndUnsubscribeModelApiRequestSuccess>(saveAndSubscribeActions.UNSAVE_AND_UNSUBSCRIBE_MODEL_API_REQUEST_SUCCESS),
		switchMap(action => [
			{
				type: saveActions.REMOVE_SAVED_STORE_MAPPING,
				portfolioId: action.portfolioId
			} as Action,
			{
				type: subscribeActions.REMOVE_SUBSCRIPTION_STORE_MAPPING,
				portfolioId: action.portfolioId
			} as Action
		])
	));

	constructor(private actions$: Actions, private userDataService: UserDataService, private userDataHandler: UserDataHandlerService, private store$: Store<any>) { }
}
